import { Check, Clock, Music, Palette, Users, Utensils } from "@/components/icons";
import type { StepperStep } from "@/components/setup/stepper";
import type { TranslationKey } from "@/i18n";
import type { GetMySetupResp } from "@/lib/types";

export type SetupStepId =
  | "basics"
  | "look"
  | "menu"
  | "timeline"
  | "dj"
  | "done";

/**
 * The labelKey is the step's canonical name: the stepper and the wizard
 * card heading both render it, so they always match. The subtitleKey is
 * the guided line under the heading (wizard mode only — standalone edit
 * pages use STEP_EDIT_PAGES instead).
 */
export const STEPS: (StepperStep & {
  id: SetupStepId;
  subtitleKey: TranslationKey;
})[] = [
  {
    id: "basics",
    labelKey: "setup.steps.basics.label",
    icon: Users,
    subtitleKey: "setup.steps.basics.subtitle",
  },
  {
    id: "look",
    labelKey: "setup.steps.look.label",
    icon: Palette,
    subtitleKey: "setup.steps.look.subtitle",
  },
  {
    id: "menu",
    labelKey: "setup.steps.menu.label",
    icon: Utensils,
    subtitleKey: "setup.steps.menu.subtitle",
  },
  {
    id: "timeline",
    labelKey: "setup.steps.timeline.label",
    icon: Clock,
    subtitleKey: "setup.steps.timeline.subtitle",
  },
  {
    id: "dj",
    labelKey: "setup.steps.dj.label",
    icon: Music,
    subtitleKey: "setup.steps.dj.subtitle",
  },
  {
    id: "done",
    labelKey: "setup.steps.done.label",
    icon: Check,
    subtitleKey: "setup.steps.done.subtitle",
  },
];

export const STEP_ORDER: SetupStepId[] = STEPS.map((s) => s.id);

export const STEP_BY_ID = Object.fromEntries(
  STEPS.map((s) => [s.id, s]),
) as Record<SetupStepId, (typeof STEPS)[number]>;

export interface SetupStepProps {
  orderId: string;
  setup: GetMySetupResp;
  /** True during the guided first run (buttons say "Save & continue"). */
  linear: boolean;
  onDone: () => void;
  onBack: (() => void) | null;
}

/**
 * Standalone edit pages under /my-orders/:orderId — one per step. The
 * segment is the URL piece; titleKey/descriptionKey feed the PageHeader
 * (the step cards hide their own headings there, see step-chrome.tsx).
 */
export const STEP_EDIT_PAGES: Record<
  SetupStepId,
  { segment: string; titleKey: TranslationKey; descriptionKey: TranslationKey }
> = {
  basics: {
    segment: "basics",
    titleKey: "setup.editPages.basics.title",
    descriptionKey: "setup.editPages.basics.description",
  },
  look: {
    segment: "theme",
    titleKey: "setup.editPages.look.title",
    descriptionKey: "setup.editPages.look.description",
  },
  menu: {
    segment: "menu",
    titleKey: "setup.editPages.menu.title",
    descriptionKey: "setup.editPages.menu.description",
  },
  timeline: {
    segment: "timeline",
    titleKey: "setup.editPages.timeline.title",
    descriptionKey: "setup.editPages.timeline.description",
  },
  dj: {
    segment: "dj",
    titleKey: "setup.editPages.dj.title",
    descriptionKey: "setup.editPages.dj.description",
  },
  done: {
    segment: "share",
    titleKey: "setup.editPages.done.title",
    descriptionKey: "setup.editPages.done.description",
  },
};

/** Reverse lookup for the route param; null for unknown segments. */
export function stepIdForSegment(segment: string): SetupStepId | null {
  return (
    (Object.keys(STEP_EDIT_PAGES) as SetupStepId[]).find(
      (id) => STEP_EDIT_PAGES[id].segment === segment,
    ) ?? null
  );
}
